import React, { useState, useEffect, useCallback } from "react";
import { getAuthHeaders } from "../utils/auth";
import { useLanguage } from "../utils/i18n";

const API = "/api";

const PLAN_LABELS = {
  free_trial: "Free Trial",
  basic:      "Basic",
  pro:        "Pro",
  expired:    "Expired",
};

const PLAN_COLORS = {
  free_trial: "#4f8ef7",
  basic:      "#38bdf8",
  pro:        "#ff69b4",
  expired:    "#ef4444",
};

export default function AdminBillingPage({ onBack }) {
  const { t } = useLanguage();
  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [nextKey, setNextKey]       = useState(null);
  const [toast, setToast]           = useState("");
  const [search, setSearch]         = useState("");

  // Modal state
  const [modal, setModal] = useState(null); // { type, user }

  // Modal form values
  const [trialDays, setTrialDays]         = useState(7);
  const [setPlanVal, setSetPlanVal]       = useState("free_trial");
  const [voiceLimit, setVoiceLimit]       = useState("");
  const [aiLimit, setAiLimit]             = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const loadUsers = useCallback(async (startKey = null) => {
    setLoading(true);
    setError("");
    try {
      const headers = await getAuthHeaders();
      const url = startKey
        ? `${API}/admin/users?last_key=${encodeURIComponent(startKey)}`
        : `${API}/admin/users`;
      const res  = await fetch(url, { headers });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Failed to load users.");
        return;
      }
      setUsers((prev) => startKey ? [...prev, ...(data.users || [])] : (data.users || []));
      setNextKey(data.next_key || null);
    } catch {
      setError("Network error loading users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // ── Actions ────────────────────────────────────────────────────────────

  const openModal = (type, user) => {
    setModal({ type, user });
    setTrialDays(7);
    setSetPlanVal(user.plan || "free_trial");
    setVoiceLimit(user.custom_voice_limit != null ? String(user.custom_voice_limit) : "");
    setAiLimit(user.custom_ai_limit != null ? String(user.custom_ai_limit) : "");
  };

  const closeModal = () => setModal(null);

  const handleExtendTrial = async () => {
    setActionLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API}/admin/users/${modal.user.userId}/extend-trial`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ days: parseInt(trialDays, 10) }),
      });
      const data = await res.json();
      if (!res.ok) { showToast(`Error: ${data.error}`); return; }
      showToast(`✅ Trial extended by ${trialDays} days (ends ${new Date(data.trial_end).toLocaleDateString()})`);
      await loadUsers();
      closeModal();
    } catch {
      showToast("Network error.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetPlan = async () => {
    setActionLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API}/admin/users/${modal.user.userId}/set-plan`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ plan: setPlanVal }),
      });
      const data = await res.json();
      if (!res.ok) { showToast(`Error: ${data.error}`); return; }
      showToast(`✅ Plan updated to ${PLAN_LABELS[setPlanVal]}`);
      await loadUsers();
      closeModal();
    } catch {
      showToast("Network error.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetLimits = async () => {
    setActionLoading(true);
    try {
      const headers = await getAuthHeaders();
      const body = {};
      if (voiceLimit !== "") body.voice_limit = voiceLimit === "default" ? null : parseInt(voiceLimit, 10);
      if (aiLimit !== "")    body.ai_limit    = aiLimit    === "default" ? null : parseInt(aiLimit, 10);
      const res = await fetch(`${API}/admin/users/${modal.user.userId}/set-limits`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { showToast(`Error: ${data.error}`); return; }
      showToast("✅ Custom limits saved");
      await loadUsers();
      closeModal();
    } catch {
      showToast("Network error.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleDocPermission = async (user) => {
    const newPerm = user.permission_to_save_docs === "Y" ? "N" : "Y";
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API}/admin/users/${user.userId}/set-doc-permission`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ permission: newPerm }),
      });
      const data = await res.json();
      if (!res.ok) { showToast(`Error: ${data.error}`); return; }
      showToast(`✅ Doc save permission set to ${newPerm} for user`);
      await loadUsers();
    } catch {
      showToast("Network error.");
    }
  };

  // ── Filter ─────────────────────────────────────────────────────────────
  const filtered = users.filter((u) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (u.userId || "").toLowerCase().includes(q) ||
      (u.email  || "").toLowerCase().includes(q) ||
      (u.plan   || "").toLowerCase().includes(q)
    );
  });

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <button style={styles.backBtn} onClick={onBack}>{t("backBtnAdmin")}</button>
        <h1 style={styles.pageTitle}>{t("adminDashboardTitle")}</h1>
        <button style={styles.refreshBtn} onClick={() => loadUsers()}>{t("refreshBtn")}</button>
      </div>

      <div style={styles.searchRow}>
        <input
          style={styles.searchInput}
          placeholder="Search by email, user ID or plan…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span style={{ color: "#64748b", fontSize: 13 }}>{filtered.length} users</span>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      {loading && !users.length ? (
        <div style={styles.loadingText}>{t("loading")}</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.thead}>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>User ID</th>
                <th style={styles.th}>Plan</th>
                <th style={styles.th}>Trial End</th>
                <th style={styles.th}>Voice</th>
                <th style={styles.th}>AI</th>
                <th style={styles.th}>Month</th>
                <th style={styles.th}>Doc Save</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const plan = u.effective_plan || u.plan || "unknown";
                const color = PLAN_COLORS[plan] || "#94a3b8";
                return (
                  <tr key={u.userId} style={styles.tr}>
                    <td style={{ ...styles.td, fontSize: 12, color: "#e2e8f0", maxWidth: 200, overflowWrap: "break-word" }}>
                      {u.email || <span style={{ color: "#475569", fontStyle: "italic" }}>—</span>}
                    </td>
                    <td style={{ ...styles.td, fontFamily: "monospace", fontSize: 11, color: "#64748b", maxWidth: 140, overflowWrap: "break-word" }}>
                      {u.userId}
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.planBadge, background: color + "28", color, border: `1px solid ${color}55` }}>
                        {u.plan_name || PLAN_LABELS[plan] || plan}
                      </span>
                    </td>
                    <td style={{ ...styles.td, fontSize: 12, color: "#94a3b8" }}>
                      {u.trial_end
                        ? new Date(u.trial_end).toLocaleDateString()
                        : "—"}
                    </td>
                    <td style={styles.tdNum}>
                      <span style={{ color: "#4f8ef7" }}>{u.voice_used ?? 0}</span>
                      <span style={{ color: "#475569" }}> / {u.voice_limit ?? "—"}</span>
                      {u.custom_voice_limit != null && (
                        <span style={styles.customTag}>custom</span>
                      )}
                    </td>
                    <td style={styles.tdNum}>
                      <span style={{ color: "#ff69b4" }}>{u.ai_used ?? 0}</span>
                      <span style={{ color: "#475569" }}> / {u.ai_limit ?? "—"}</span>
                      {u.custom_ai_limit != null && (
                        <span style={styles.customTag}>custom</span>
                      )}
                    </td>
                    <td style={{ ...styles.td, fontSize: 12, color: "#94a3b8" }}>
                      {u.usage_month || "—"}
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        display: "inline-block",
                        padding: "3px 10px",
                        borderRadius: 20,
                        fontSize: 11,
                        fontWeight: 700,
                        background: u.permission_to_save_docs === "Y" ? "rgba(34,197,94,0.15)" : "rgba(148,163,184,0.1)",
                        color: u.permission_to_save_docs === "Y" ? "#22c55e" : "#64748b",
                        border: `1px solid ${u.permission_to_save_docs === "Y" ? "#22c55e55" : "#33415555"}`,
                      }}>
                        {u.permission_to_save_docs === "Y" ? "✓ Yes" : "✗ No"}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionBtns}>
                        <button style={{ ...styles.actionBtn, background: "#1e40af" }}
                          onClick={() => openModal("extend", u)}>
                          Extend Trial
                        </button>
                        <button style={{ ...styles.actionBtn, background: "#7c3aed" }}
                          onClick={() => openModal("plan", u)}>
                          Set Plan
                        </button>
                        <button style={{ ...styles.actionBtn, background: "#065f46" }}
                          onClick={() => openModal("limits", u)}>
                          Set Limits
                        </button>
                        <button
                          style={{ ...styles.actionBtn, background: u.permission_to_save_docs === "Y" ? "#7f1d1d" : "#14532d" }}
                          onClick={() => handleToggleDocPermission(u)}>
                          {u.permission_to_save_docs === "Y" ? "Revoke Doc Save" : "Grant Doc Save"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {nextKey && (
        <button style={styles.loadMoreBtn} onClick={() => loadUsers(nextKey)} disabled={loading}>
          {loading ? "Loading…" : "Load more"}
        </button>
      )}

      {/* ── Modals ─────────────────────────────────────────────────── */}
      {modal && (
        <div style={styles.modalOverlay} onClick={closeModal}>
          <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            {modal.type === "extend" && (
              <>
                <h3 style={styles.modalTitle}>Extend Free Trial</h3>
                <p style={styles.modalSub}>User: <code style={styles.code}>{modal.user.email || modal.user.userId}</code></p>
                <label style={styles.label}>Additional days</label>
                <input type="number" min={1} max={365} value={trialDays}
                  onChange={(e) => setTrialDays(e.target.value)}
                  style={styles.input} />
                <div style={styles.modalBtns}>
                  <button style={{ ...styles.modalBtn, background: "#1e40af" }}
                    onClick={handleExtendTrial} disabled={actionLoading}>
                    {actionLoading ? "Saving…" : "Extend Trial"}
                  </button>
                  <button style={{ ...styles.modalBtn, background: "#334155" }} onClick={closeModal}>Cancel</button>
                </div>
              </>
            )}
            {modal.type === "plan" && (
              <>
                <h3 style={styles.modalTitle}>Set Plan</h3>
                <p style={styles.modalSub}>User: <code style={styles.code}>{modal.user.email || modal.user.userId}</code></p>
                <label style={styles.label}>Plan</label>
                <select value={setPlanVal} onChange={(e) => setSetPlanVal(e.target.value)} style={styles.input}>
                  <option value="free_trial">Free Trial (7 days from now)</option>
                  <option value="basic">Basic</option>
                  <option value="pro">Pro</option>
                  <option value="expired">Expired</option>
                </select>
                <div style={styles.modalBtns}>
                  <button style={{ ...styles.modalBtn, background: "#7c3aed" }}
                    onClick={handleSetPlan} disabled={actionLoading}>
                    {actionLoading ? "Saving…" : "Set Plan"}
                  </button>
                  <button style={{ ...styles.modalBtn, background: "#334155" }} onClick={closeModal}>Cancel</button>
                </div>
              </>
            )}
            {modal.type === "limits" && (
              <>
                <h3 style={styles.modalTitle}>Override Usage Limits</h3>
                <p style={styles.modalSub}>User: <code style={styles.code}>{modal.user.email || modal.user.userId}</code></p>
                <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 14 }}>
                  Leave blank to keep current value. Type <strong>"default"</strong> to remove override.
                </p>
                <label style={styles.label}>Voice translations / month</label>
                <input type="text" placeholder={`Current: ${modal.user.voice_limit}`}
                  value={voiceLimit} onChange={(e) => setVoiceLimit(e.target.value)}
                  style={styles.input} />
                <label style={{ ...styles.label, marginTop: 10 }}>AI calls / month</label>
                <input type="text" placeholder={`Current: ${modal.user.ai_limit}`}
                  value={aiLimit} onChange={(e) => setAiLimit(e.target.value)}
                  style={styles.input} />
                <div style={styles.modalBtns}>
                  <button style={{ ...styles.modalBtn, background: "#065f46" }}
                    onClick={handleSetLimits} disabled={actionLoading}>
                    {actionLoading ? "Saving…" : "Save Limits"}
                  </button>
                  <button style={{ ...styles.modalBtn, background: "#334155" }} onClick={closeModal}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={styles.toast}>{toast}</div>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#0f1624",
    color: "#e2e8f0",
    padding: "24px 20px",
    boxSizing: "border-box",
  },
  topBar: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginBottom: 24,
  },
  backBtn: {
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.12)",
    color: "#cbd5e1",
    borderRadius: 8,
    padding: "7px 14px",
    cursor: "pointer",
    fontSize: 14,
  },
  refreshBtn: {
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.12)",
    color: "#cbd5e1",
    borderRadius: 8,
    padding: "7px 14px",
    cursor: "pointer",
    fontSize: 14,
    marginLeft: "auto",
  },
  pageTitle: {
    fontSize: 20,
    fontWeight: 700,
    margin: 0,
    flex: 1,
  },
  searchRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 20,
  },
  searchInput: {
    flex: 1,
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8,
    padding: "8px 14px",
    color: "#e2e8f0",
    fontSize: 14,
    outline: "none",
  },
  errorBox: {
    background: "rgba(239,68,68,0.15)",
    border: "1px solid rgba(239,68,68,0.4)",
    borderRadius: 10,
    padding: "12px 18px",
    color: "#f87171",
    fontSize: 14,
    marginBottom: 16,
  },
  loadingText: { color: "#64748b", textAlign: "center", padding: 40 },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
    minWidth: 800,
  },
  thead: { borderBottom: "1px solid rgba(255,255,255,0.1)" },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    color: "#64748b",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  tr: { borderBottom: "1px solid rgba(255,255,255,0.05)" },
  td: { padding: "10px 12px", verticalAlign: "middle" },
  tdNum: { padding: "10px 12px", verticalAlign: "middle", fontVariantNumeric: "tabular-nums" },
  planBadge: {
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.3,
  },
  customTag: {
    display: "inline-block",
    marginLeft: 6,
    fontSize: 10,
    color: "#fbbf24",
    background: "rgba(251,191,36,0.15)",
    borderRadius: 4,
    padding: "1px 5px",
  },
  actionBtns: { display: "flex", gap: 6, flexWrap: "wrap" },
  actionBtn: {
    padding: "5px 10px",
    fontSize: 11,
    fontWeight: 600,
    color: "#fff",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  loadMoreBtn: {
    display: "block",
    margin: "20px auto",
    padding: "10px 28px",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.12)",
    color: "#cbd5e1",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14,
  },
  // Modal
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.65)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modalBox: {
    background: "#1e293b",
    borderRadius: 14,
    padding: "28px 28px 22px",
    width: "100%",
    maxWidth: 420,
    border: "1px solid rgba(255,255,255,0.1)",
  },
  modalTitle: { margin: "0 0 6px", fontSize: 18, fontWeight: 700 },
  modalSub: { color: "#94a3b8", fontSize: 13, marginBottom: 16 },
  code: {
    background: "rgba(255,255,255,0.06)",
    borderRadius: 4,
    padding: "2px 6px",
    fontFamily: "monospace",
    fontSize: 12,
  },
  label: { display: "block", color: "#94a3b8", fontSize: 13, marginBottom: 6 },
  input: {
    width: "100%",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8,
    padding: "9px 12px",
    color: "#e2e8f0",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
  },
  modalBtns: { display: "flex", gap: 10, marginTop: 20 },
  modalBtn: {
    flex: 1,
    padding: "10px 0",
    borderRadius: 8,
    border: "none",
    color: "#fff",
    fontWeight: 700,
    fontSize: 14,
    cursor: "pointer",
  },
  toast: {
    position: "fixed",
    bottom: 30,
    left: "50%",
    transform: "translateX(-50%)",
    background: "#1e293b",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 10,
    padding: "12px 24px",
    color: "#e2e8f0",
    fontSize: 14,
    fontWeight: 600,
    zIndex: 2000,
    boxShadow: "0 8px 30px rgba(0,0,0,0.5)",
    whiteSpace: "nowrap",
  },
};
